'use client';

import { useConfig, useLocale } from '@payloadcms/ui';
import { getRouteResolver } from '@websolutespa/payload-utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageService } from '../../api/message.service';
import { LlmAppCredentials, LlmMessage, PythonStreamResponse } from '../../api/types';
import { LlmChunk } from './LlmChunk';

export type LlmChatCompletionProps = Omit<LlmAppCredentials, 'httpReferrers'> & {
  completion?: LlmMessage[];
  test?: boolean;
  threadId?: string;
  onMessage?: (response: PythonStreamResponse) => void;
  onEnd?: (response: PythonStreamResponse) => void;
  onError?: (error: any) => void;
};

export const LlmChatCompletion: React.FC<LlmChatCompletionProps> = ({
  apiKey,
  appKey,
  completion,
  test = false,
  threadId,
  onMessage,
  onEnd,
  onError,
}: LlmChatCompletionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { config } = useConfig();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PythonStreamResponse>();
  const locale = useLocale();
  const routeResolver = getRouteResolver(config);

  const messageService = useMemo(() => {
    const url = routeResolver.api(`/llm/message?locale=${locale}`);
    return new MessageService({
      url,
    });
  }, [locale]);

  useEffect(() => {
    const fetchData = async (messages: LlmMessage[], test: boolean = false) => {
      try {
        setLoading(true);
        const sendMessagePayload = {
          apiKey,
          appKey,
          messages,
          test,
          threadId,
        };
        await messageService.sendMessage(sendMessagePayload,
          // onMessage
          (response: PythonStreamResponse) => {
            const meaningChunks = response.chunks.filter(x =>
              typeof x === 'string'
              || !['header', 'info', 'log', 'end'].includes(x.type)
            );
            // console.log('meaningChunks', meaningChunks);
            if (meaningChunks.length > 0) {
              setResponse(response);
              if (typeof onMessage === 'function') {
                onMessage(response);
              }
              /*
              if (ref.current) {
                ref.current.scrollIntoView();
              }
              */
            }
          },
          // onEnd
          (response: PythonStreamResponse) => {
            // console.log('LlmChatCompletion.response', response);
            setLoading(false);
            if (typeof onEnd === 'function') {
              onEnd(response);
            }
          },
          // onChunk
          (chunks) => {
            // console.log('onChunk', chunks);
          });
      } catch (error: any) {
        console.log(`LlmChatCompletion.onSubmit.error ${error.message}.`);
        setLoading(false);
        if (typeof onError === 'function') {
          onError(error);
        }
      }
    };
    if (completion) {
      fetchData(completion, test);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiKey,
    appKey,
    completion,
    messageService,
    onEnd,
    onError,
    // onMessage,
    test,
    threadId,
  ]);

  return (
    <div className="llm__chatCompletion">
      {response && (
        <div ref={ref} className="llm__thread assistant">
          <div className="llm__message">
            {response.chunks.map((x, i) => (
              <LlmChunk key={i} item={x} />
            ))}
          </div>
        </div>
      )}
      {loading && (
        <div className="llm__busyIndicator">
          <span></span>
        </div>
      )}
    </div>
  );
};
